import TextField from "components/crm/ui/TextField";
import RadioCardList from "components/radioCardList/RadioCardList";

export const STANDARD = "standard";
export const NEW_CONTACT = "new";
export const EMPTY_CONTACT = { firstname: "", lastname: "", position: "", phone: "" };

// A person of the company card (prospectionView `members`): an account by its
// user, a contact of the company's file by its own id (JBWEB-1427).
export const memberRef = (member) =>
  member.account ? { userId: member.id } : { contactId: member.id };
export const refKey = (ref) =>
  ref?.userId ? `u:${ref.userId}` : ref?.contactId ? `c:${ref.contactId}` : STANDARD;

// The choice a sheet starts on: the person its callback names (the next calls
// stay on the direct line), else the standard.
export const initialCallbackWho = (line) => ({ choice: refKey(line?.contactRef), contact: EMPTY_CONTACT });

const fullName = (person) => [person.firstname, person.lastname].filter(Boolean).join(" ");

// A new contact needs a name, a position (the api's rule) and the line to call.
export const isCallbackWhoReady = ({ choice, contact }) =>
  choice !== NEW_CONTACT ||
  (Boolean(contact.firstname.trim() || contact.lastname.trim()) &&
    Boolean(contact.position.trim()) &&
    Boolean(contact.phone.trim()));

// The person to call back, as followUp.contactRef sends it: null for the
// standard (JBWEB-1459).
export const callbackRefOf = (choice, people) => {
  if (choice === STANDARD) return null;
  const person = people.find((member) => refKey(memberRef(member)) === choice);
  return person ? memberRef(person) : null;
};

const Line = ({ title, detail }) => (
  <span className="flex flex-col">
    <span className="text-sm font-medium text-gray-900">{title}</span>
    {detail && <span className="text-xs text-gray-600">{detail}</span>}
  </span>
);

/**
 * « Rappeler qui ? » of « À rappeler »: the standard, someone of the company
 * with a phone, or a new contact (the direct line the switchboard gave),
 * added to the company's contacts on confirmation. `value`: { choice,
 * contact } (initialCallbackWho); `standardPhone`: the switchboard's number.
 */
const CallbackWhoField = ({ people, standardPhone, value, onChange }) => {
  const reachable = people.filter((person) => person.phone);
  const setContact = (field) => (event) =>
    onChange({ ...value, contact: { ...value.contact, [field]: event.target.value } });
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold leading-5 text-ink">Rappeler qui ?</p>
      <RadioCardList
        name="callback-who"
        label="Rappeler qui ?"
        value={value.choice}
        onChange={(choice) => onChange({ ...value, choice })}
        options={[
          { value: STANDARD, label: <Line title="Le standard" detail={standardPhone || "Aucun numéro"} /> },
          ...reachable.map((person) => ({
            value: refKey(memberRef(person)),
            label: <Line title={fullName(person)} detail={[person.position, person.phone].filter(Boolean).join(" · ")} />,
          })),
          {
            value: NEW_CONTACT,
            label: <Line title="Nouveau contact" detail="La ligne directe donnée par le standard" />,
            // Its fields in its own line, once it is chosen.
            content: (
              <div className="grid grid-cols-2 gap-3">
                <TextField id="callback-firstname" label="Prénom" value={value.contact.firstname} onChange={setContact("firstname")} />
                <TextField id="callback-lastname" label="Nom" value={value.contact.lastname} onChange={setContact("lastname")} />
                <TextField id="callback-position" label="Poste" value={value.contact.position} onChange={setContact("position")} />
                <TextField id="callback-phone" type="tel" label="Ligne directe" value={value.contact.phone} onChange={setContact("phone")} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default CallbackWhoField;
